import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import {
  LayoutDashboard,
  Home,
  BookOpen,
  Tent,
  HelpCircle,
  Quote,
  Phone,
  Megaphone,
  Inbox,
  BarChart3,
  KeyRound,
  HeartHandshake,
  LogOut,
  ExternalLink,
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/homepage", label: "Homepage", icon: Home },
  { to: "/admin/programs", label: "Programs", icon: BookOpen },
  { to: "/admin/camps", label: "Camps", icon: Tent },
  { to: "/admin/faqs", label: "FAQ", icon: HelpCircle },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/contact-info", label: "Contact info", icon: Phone },
  { to: "/admin/therapy", label: "Therapy Booking", icon: HeartHandshake },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { to: "/admin/account", label: "Account", icon: KeyRound },
];

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/admin" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white font-display font-extrabold text-sm">
              Nx
            </span>
            <div>
              <p className="font-display text-base font-extrabold tracking-tight leading-none">
                Nexora
              </p>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                Website Content
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
              data-testid="admin-view-site"
            >
              View site
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <span className="hidden sm:block text-xs text-slate-500">
              {admin?.email}
            </span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-xs font-bold hover:bg-slate-800"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3" data-testid="admin-sidenav">
          <nav className="rounded-2xl bg-white border border-slate-200 p-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors mb-0.5 ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="lg:col-span-9 min-w-0" data-testid="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
}
